import { z } from 'zod'
import type { AiNavTarget } from './navigation'
import type { AiFormFillTarget } from './forms'

export * from './navigation'
export * from './forms'

/**
 * Yapay Zekâ asistanı — masaüstü backend, portal ve frontend ortak tipleri.
 *
 * Para birimi kuralı (§7): maliyet ve kredi hiçbir yerde float tutulmaz.
 *   - Fiyatlar USD/1M token olarak ondalık METİN ("2.50") — en çok 3 ondalık.
 *   - Maliyet tamsayı nano-USD (1e-9 USD); kredi tamsayı mikro-kredi (1e-6 kredi). DB'de BIGINT.
 */

// ─── Modeller ve fiyatlar ─────────────────────────────────────────────────────

/** Varsayılan modeller (K4): ajan için Sonnet, başlık/özet için Haiku. Portal ayarıyla değişir. */
export const AI_DEFAULT_MODELS = {
  agent: 'claude-sonnet-5-5',
  light: 'claude-haiku-5-5',
} as const

export interface AiModelPrice {
  /** USD / 1M token, ondalık metin */
  inputPerMtok: string
  outputPerMtok: string
  cacheReadPerMtok: string
  cacheWritePerMtok: string
}

/**
 * Varsayılan fiyat tablosu (Anthropic 1. taraf API, 2026-10). Portal `ai.pricing` ayarı bunu ezer —
 * kodda fiyat güncellemek gerekmez; bu tablo yalnızca ayar boşken kullanılır.
 * Cache yazma = 5 dk TTL (girişin 1,25 katı).
 */
export const AI_DEFAULT_PRICING: Record<string, AiModelPrice> = {
  'claude-opus-5-5': { inputPerMtok: '4.00', outputPerMtok: '20.00', cacheReadPerMtok: '0.20', cacheWritePerMtok: '5.00' },
  'claude-sonnet-5-5': { inputPerMtok: '2.00', outputPerMtok: '10.00', cacheReadPerMtok: '0.20', cacheWritePerMtok: '2.50' },
  'claude-haiku-5-5': { inputPerMtok: '0.10', outputPerMtok: '0.50', cacheReadPerMtok: '0.01', cacheWritePerMtok: '0.125' },
  // DeepSeek (api-docs.deepseek.com, 2026-10): TEPE saat fiyatı (kullanım saate göre değişir; tepe = en yüksek).
  // Cache yazma ayrı ücretlenmez → cache-miss giriş fiyatı.
  'deepseek-flash': { inputPerMtok: '0.30', outputPerMtok: '1.20', cacheReadPerMtok: '0.006', cacheWritePerMtok: '0.30' },
  'deepseek-v4-pro': { inputPerMtok: '1.32', outputPerMtok: '3.96', cacheReadPerMtok: '0.044', cacheWritePerMtok: '1.32' },
  // Google Gemini (ai.google.dev, 2026-10; metin, standart katman). 3.8-flash: 2027-01-01'den itibaren giriş 1.50 / çıkış 7.50 / cache 0.15.
  // 3.1-pro-preview: 200k token altı fiyatı. Cache yazma ayrı ücretlenmez → giriş fiyatı.
  'gemini-3.8-flash': { inputPerMtok: '0.75', outputPerMtok: '3.75', cacheReadPerMtok: '0.075', cacheWritePerMtok: '0.75' },
  'gemini-3.1-flash-lite': { inputPerMtok: '0.25', outputPerMtok: '1.50', cacheReadPerMtok: '0.025', cacheWritePerMtok: '0.25' },
  'gemini-3.1-pro-preview': { inputPerMtok: '2.00', outputPerMtok: '12.00', cacheReadPerMtok: '0.20', cacheWritePerMtok: '2.00' },
}

/** Anthropic `usage` alanlarıyla aynı anlam: `inputTokens` cache DIŞI giriş (cache okuma/yazma ayrı). */
export interface AiTokenUsage {
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
}

export const emptyUsage = (): AiTokenUsage => ({ inputTokens: 0, outputTokens: 0, cacheReadTokens: 0, cacheWriteTokens: 0 })

export function addUsage(a: AiTokenUsage, b: AiTokenUsage): AiTokenUsage {
  return {
    inputTokens: a.inputTokens + b.inputTokens,
    outputTokens: a.outputTokens + b.outputTokens,
    cacheReadTokens: a.cacheReadTokens + b.cacheReadTokens,
    cacheWriteTokens: a.cacheWriteTokens + b.cacheWriteTokens,
  }
}

const PRICE_RE = /^(\d{1,6})(?:\.(\d{1,3}))?$/

/** "2.50" USD/1M token → token başına nano-USD (2500). Float kullanılmaz. */
export function usdPerMtokToNanoPerToken(price: string): number {
  const m = PRICE_RE.exec(price.trim())
  if (!m) throw new Error(`Geçersiz fiyat: ${price} (en çok 3 ondalıklı USD bekleniyor)`)
  const whole = Number.parseInt(m[1] ?? '0', 10)
  const frac = Number.parseInt((m[2] ?? '').padEnd(3, '0'), 10)
  return whole * 1000 + frac
}

export const isValidPrice = (price: string): boolean => PRICE_RE.test(price.trim())

const USD_RE = /^(\d{1,9})(?:\.(\d{1,3}))?$/

/** "50.25" USD → tamsayı nano-USD (50_250_000_000). Float kullanılmaz. */
export function usdToNano(usd: string): number {
  const m = USD_RE.exec(usd.trim())
  if (!m) throw new Error(`Geçersiz tutar: ${usd} (en çok 3 ondalıklı USD bekleniyor)`)
  return Number.parseInt(m[1] ?? '0', 10) * 1_000_000_000 + Number.parseInt((m[2] ?? '').padEnd(3, '0'), 10) * 1_000_000
}

export const isValidUsd = (usd: string): boolean => USD_RE.test(usd.trim())

/** Tek LLM çağrısının maliyeti — tamsayı nano-USD (AIA `Pricing.cost` formülünün tamsayı karşılığı). */
export function costNanoUsd(usage: AiTokenUsage, price: AiModelPrice): number {
  return (
    usage.inputTokens * usdPerMtokToNanoPerToken(price.inputPerMtok) +
    usage.outputTokens * usdPerMtokToNanoPerToken(price.outputPerMtok) +
    usage.cacheReadTokens * usdPerMtokToNanoPerToken(price.cacheReadPerMtok) +
    usage.cacheWriteTokens * usdPerMtokToNanoPerToken(price.cacheWritePerMtok)
  )
}

/**
 * Maliyet → mikro-kredi. `creditsPerUsd` çevrim oranıdır; Pusula'da sabit 1'dir (bakiye doğrudan USD, mikro-USD);
 * `marginPercent` (tamsayı %, ör. 20) sağlayıcı maliyetinin üzerine kâr/vergi/masraf payı ekler.
 * Yukarı yuvarlanır: kesir kadar bile kullanım ücretsiz geçmez.
 */
export function microCreditsFor(costNano: number, creditsPerUsd: number, marginPercent = 0): number {
  if (!Number.isInteger(costNano) || !Number.isInteger(creditsPerUsd) || !Number.isInteger(marginPercent)) throw new Error('Tamsayı bekleniyor')
  return Math.ceil((costNano * creditsPerUsd * (100 + marginPercent)) / 100_000)
}

/** Mikro-kredi → "12.35" (2 ondalık, yukarı yuvarlanmadan kırpılır; float yok). Negatif destekler. */
export function formatCredits(micro: number, decimals = 2): string {
  const sign = micro < 0 ? '-' : ''
  const abs = Math.abs(Math.trunc(micro))
  const whole = Math.floor(abs / 1_000_000)
  const frac = String(abs % 1_000_000).padStart(6, '0').slice(0, decimals)
  return decimals > 0 ? `${sign}${whole}.${frac}` : `${sign}${whole}`
}

/** "12.5" kredi metni → mikro-kredi (Super Admin kredi yükleme). En çok 6 ondalık. */
export function parseCredits(value: string): number {
  const m = /^(-)?(\d{1,9})(?:\.(\d{1,6}))?$/.exec(value.trim())
  if (!m) throw new Error(`Geçersiz kredi: ${value}`)
  const micro = Number.parseInt(m[2] ?? '0', 10) * 1_000_000 + Number.parseInt((m[3] ?? '').padEnd(6, '0'), 10)
  return m[1] ? -micro : micro
}

/** nano-USD → "0.0123" USD (4 ondalık, bilgi amaçlı gösterim) */
export function formatUsd(nano: number): string {
  const abs = Math.abs(Math.trunc(nano))
  const whole = Math.floor(abs / 1_000_000_000)
  const frac = String(abs % 1_000_000_000).padStart(9, '0').slice(0, 4)
  return `${nano < 0 ? '-' : ''}${whole}.${frac}`
}

// ─── Portal proxy sözleşmesi ──────────────────────────────────────────────────

/** Masaüstü → portal kimlik bilgisi (heartbeat ile aynı aktivasyon sırrı) */
export interface AiProxyCredentials {
  portalUrl: string
  activationId: string
  activationSecret: string
  machineHash: string
}

/** `GET /api/v1/ai-proxy/status` */
export interface AiProxyStatus {
  enabled: boolean
  /** Super Admin bu lisansa kendi sağlayıcısını (BYOK / yerel model) kullanma izni verdi mi */
  allowOwnProvider: boolean
  balanceMicro: number
  models: { agent: string; light: string }
  pricing: Record<string, AiModelPrice>
  creditsPerUsd: number
  /** Sağlayıcı maliyetine eklenen kâr marjı, tamsayı % (kullanıcıdan düşülen krediye yansır) */
  marginPercent: number
  /** Portalda görsel kabul eden bir model var mı (ajan ya da hafif model) */
  vision: boolean
  /** Ajan modeli görsel kabul ediyor mu; etmiyorsa görselli turlarda hafif model (görsel destekliyorsa) kullanılır */
  agentVision: boolean
  /** Ajan modelinin bağlam penceresi (token): sağlayıcının model listesinden, yoksa ön ayardan */
  contextWindow: number
  /** Sesli giriş (dikte) etkin: Super Admin bir ses motoru ve anahtarı tanımladı */
  stt: boolean
}

/** Proxy akış yanıtının başlıkları (gövde = Anthropic MessageStream NDJSON) */
export const AI_PROXY_HEADERS = {
  requestId: 'x-pusula-ai-request',
  price: 'x-pusula-ai-price',
  creditsPerUsd: 'x-pusula-ai-credits-per-usd',
  marginPercent: 'x-pusula-ai-margin-percent',
  balance: 'x-pusula-ai-balance',
} as const

export type AiProxyPurpose = 'chat' | 'title' | 'summary'

// ─── Sağlayıcılar (BYOK) ──────────────────────────────────────────────────────

/**
 * portal = Pusula kredisi (varsayılan). Diğerleri firmanın kendi anahtarıyla / yerel modeliyle çalışır ve
 * yalnızca Super Admin lisansa izin verdiyse seçilebilir. Kendi sağlayıcıda Pusula kredisi düşülmez;
 * token tüketimi yine kaydedilir ve gösterilir.
 */
export const AI_PROVIDERS = ['portal', 'anthropic', 'openai', 'gemini', 'deepseek', 'ollama', 'openai_compatible'] as const
export type AiProviderKind = (typeof AI_PROVIDERS)[number]

export interface AiProviderPreset {
  /** OpenAI-uyumlu uç (anthropic için yok) */
  baseUrl: string | null
  needsKey: boolean
  /** adres kullanıcı tarafından değiştirilebilir mi */
  editableUrl: boolean
  /** Sağlayıcı seçilince modele otomatik yazılır; kullanıcı değiştirebilir */
  defaultAgentModel: string | null
  defaultLightModel: string | null
  /** Sohbette görsel (vision) kabul eder mi. Model başına yetenek sağlayıcının /models listesinden okunur (DeepSeek: flash görsel alır, v4-pro almaz); bu değer yalnızca liste alınamazsa/bildirmezse geçerli. Ollama/özel adres: modele bağlı, denemeye açık */
  vision: boolean
  /** Sağlayıcı modelinin bağlam penceresi (token); kademeli bağlam yönetimi buna göre eşik hesaplar. Yerel/özel modelde en küçük güvenli değer */
  contextWindow: number
}

/** Çalışma bağlamı bu değeri aşmaz: çok büyük pencereli modellerde de eski geçmiş bu sınırda özetlenir (maliyet + dikkat) */
export const AI_MAX_WORKING_CONTEXT = 200_000

export const AI_PROVIDER_PRESETS: Record<Exclude<AiProviderKind, 'portal'>, AiProviderPreset> = {
  anthropic: { baseUrl: null, needsKey: true, editableUrl: false, defaultAgentModel: AI_DEFAULT_MODELS.agent, defaultLightModel: AI_DEFAULT_MODELS.light, vision: true, contextWindow: 200_000 },
  openai: { baseUrl: 'https://api.openai.com/v1', needsKey: true, editableUrl: false, defaultAgentModel: null, defaultLightModel: null, vision: true, contextWindow: 128_000 },
  gemini: { baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai', needsKey: true, editableUrl: false, defaultAgentModel: 'gemini-3.8-flash', defaultLightModel: 'gemini-3.1-flash-lite', vision: true, contextWindow: 200_000 },
  deepseek: { baseUrl: 'https://api.deepseek.com/v1', needsKey: true, editableUrl: false, defaultAgentModel: 'deepseek-v4-pro', defaultLightModel: 'deepseek-flash', vision: false, contextWindow: 128_000 },
  ollama: { baseUrl: 'http://127.0.0.1:11434/v1', needsKey: false, editableUrl: true, defaultAgentModel: null, defaultLightModel: null, vision: true, contextWindow: 32_000 },
  openai_compatible: { baseUrl: null, needsKey: false, editableUrl: true, defaultAgentModel: null, defaultLightModel: null, vision: true, contextWindow: 32_000 },
}

/**
 * Portal proxy'sinin (Pusula kredisiyle) kullanabileceği geliştirici sağlayıcıları — Super Admin seçer.
 * Anthropic dışındakiler OpenAI-uyumlu uçtan çağrılır; seçilen modellerin fiyatı Super Admin'ce girilmelidir
 * (fiyatı olmayan modelle çağrı yapılmaz — ücretsiz geçmesin diye).
 */
export const AI_PORTAL_PROVIDERS = ['anthropic', 'openai', 'gemini', 'deepseek'] as const
export type AiPortalProvider = (typeof AI_PORTAL_PROVIDERS)[number]

/** Model adı öneki → sağlayıcı (kullanım defterinde sağlayıcı sütunu yok; sağlayıcı bakiyesi tahmini için) */
export const AI_MODEL_PREFIXES: Record<AiPortalProvider, readonly string[]> = {
  anthropic: ['claude'],
  openai: ['gpt', 'o1', 'o3', 'o4', 'chatgpt'],
  gemini: ['gemini'],
  deepseek: ['deepseek'],
}

/** Super Admin'in sağlayıcıya yüklediği bakiye: girildiği andan sonraki sağlayıcı maliyeti düşülerek kalan hesaplanır */
export interface AiProviderBalanceView {
  provider: AiPortalProvider
  /** Girilen bakiye (USD, 3 ondalık metin) */
  loadedUsd: string
  /** Girildiği an (ISO) */
  since: string
  /** O andan beri sağlayıcı maliyeti (USD) ve kalan tahmini */
  spentUsd: string
  remainingUsd: string
}

// ─── Sohbet API'si (masaüstü backend ↔ frontend) ─────────────────────────────

/** Açık 3B model görüntüleyici: hangi IFC dosyası, hangi elemanlar seçili (GlobalId) */
export const AI_IFC_SELECTION_MAX = 2000
export const AiIfcContextSchema = z.object({
  fileId: z.string().uuid(),
  selected: z.array(z.string().regex(/^[A-Za-z0-9_$-]{1,64}$/)).max(AI_IFC_SELECTION_MAX).default([]),
})
export type AiIfcContext = z.infer<typeof AiIfcContextSchema>

/** Kullanıcının ekranda açık formu: anahtar + o anki alan değerleri (sohbet mesajıyla birlikte LLM'e gider) */
export const AI_FORM_CONTEXT_MAX_FIELDS = 40
export const AiFormContextSchema = z.object({
  key: z.string().max(60),
  title: z.string().max(100).optional(),
  values: z.record(
    z.string().max(60),
    z.union([z.string().max(1000), z.number(), z.boolean(), z.null(), z.array(z.string().max(100)).max(20)]),
  ).refine((v) => Object.keys(v).length <= AI_FORM_CONTEXT_MAX_FIELDS, { message: 'Çok fazla form alanı' }),
})
export type AiFormContext = z.infer<typeof AiFormContextSchema>

export const AiPageContextSchema = z.object({
  route: z.string().max(300),
  title: z.string().max(200).optional(),
  entityType: z.string().max(60).optional(),
  entityId: z.string().max(60).optional(),
  ifc: AiIfcContextSchema.optional(),
  form: AiFormContextSchema.optional(),
})
export type AiPageContext = z.infer<typeof AiPageContextSchema>

/**
 * Sohbete eklenen görseller. İstemci uzun kenarı `maxEdge` px'e indirip JPEG'e çevirir (belirteç ve bant genişliği
 * tasarrufu); sunucu yine de boyutu ve dosya imzasını (magic bytes) doğrular — bildirilen türe güvenilmez.
 */
export const AI_IMAGE_LIMITS = {
  /** Mesaj başına en çok görsel */
  maxPerMessage: 4,
  /** Çözülmüş (ikili) en büyük boyut — Anthropic sınırı 5 MB */
  maxBytes: 4 * 1024 * 1024,
  /** İstemci yeniden boyutlandırma: uzun kenar (px) */
  maxEdge: 1568,
  mediaTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  /** Bağlamda (LLM'e giden geçmişte) tutulan en çok görsel; eskiler metin notuna çevrilir */
  maxInContext: 10,
} as const
export type AiImageMediaType = (typeof AI_IMAGE_LIMITS.mediaTypes)[number]

/** Yalnızca görsel gönderildiğinde LLM'e giden metin (arayüz bu metni göstermez) */
export const AI_IMAGE_ONLY_TEXT = '(Yalnızca görsel gönderildi)'

export const AiChatImageSchema = z.object({
  name: z.string().trim().min(1).max(120),
  mediaType: z.enum(AI_IMAGE_LIMITS.mediaTypes),
  /** base64 (başlıksız, `data:` öneki yok) */
  data: z.string().min(8).max(Math.ceil((AI_IMAGE_LIMITS.maxBytes * 4) / 3) + 8).regex(/^[A-Za-z0-9+/]+={0,2}$/),
})
export type AiChatImage = z.infer<typeof AiChatImageSchema>

/**
 * Sohbete eklenen belgeler (Word/PDF/Excel/CSV). Tür dosya İÇERİĞİNDEN belirlenir (uzantıya güvenilmez);
 * belge sunucuda saklanır, LLM'e kendiliğinden girmez: `document_read` / `document_tables` araçlarıyla parça parça okunur.
 */
export const AI_DOC_LIMITS = {
  maxPerMessage: 3,
  /** Ham dosya boyutu */
  maxBytes: 10 * 1024 * 1024,
  kinds: ['docx', 'pdf', 'xlsx', 'csv'],
  /** Dosya seçicide kabul edilen uzantılar */
  accept: '.docx,.pdf,.xlsx,.csv,.txt',
} as const
export type AiDocKind = (typeof AI_DOC_LIMITS.kinds)[number]

/** Yalnızca belge gönderildiğinde LLM'e giden metin (arayüz bu metni göstermez) */
export const AI_DOC_ONLY_TEXT = '(Yalnızca belge gönderildi)'

export const AiChatDocumentSchema = z.object({
  name: z.string().trim().min(1).max(200),
  /** base64 (başlıksız, `data:` öneki yok) */
  data: z.string().min(8).max(Math.ceil((AI_DOC_LIMITS.maxBytes * 4) / 3) + 8).regex(/^[A-Za-z0-9+/]+={0,2}$/),
})
export type AiChatDocument = z.infer<typeof AiChatDocumentSchema>

export const AiChatRequestSchema = z.object({
  conversationId: z.string().uuid().optional(),
  message: z.string().trim().max(8000),
  images: z.array(AiChatImageSchema).max(AI_IMAGE_LIMITS.maxPerMessage).default([]),
  documents: z.array(AiChatDocumentSchema).max(AI_DOC_LIMITS.maxPerMessage).default([]),
  pageContext: AiPageContextSchema.optional(),
  /** İstemci türü: mobilde ekran açma/form doldurma yoktur (anlık olay kanalı bulunmaz) */
  client: z.enum(['mobile']).optional(),
  /** Otomatik kaydet: yalnızca "oluştur" işlemleri onay kartı olmadan yürütülür (geri alınabilir); silme/geçiş/güncelleme yine onaya düşer */
  autoSave: z.boolean().default(false),
}).refine((v) => v.message.length > 0 || v.images.length > 0 || v.documents.length > 0, { message: 'Mesaj, görsel ya da belge gerekli' })
export type AiChatRequest = z.infer<typeof AiChatRequestSchema>

export const AiConversationUpdateSchema = z.object({ title: z.string().trim().min(1).max(200) })

export const AiSettingsSchema = z.object({
  enabled: z.boolean(),
  provider: z.enum(AI_PROVIDERS).default('portal'),
  baseUrl: z.string().trim().url().max(300).nullable().optional(),
  /** undefined = değiştirme, null = sil, metin = yeni anahtar */
  apiKey: z.string().trim().min(1).max(500).nullable().optional(),
  agentModel: z.string().trim().max(100).nullable().optional(),
  lightModel: z.string().trim().max(100).nullable().optional(),
  /** Kullanıcı başı aylık kredi sınırı; null = sınırsız */
  monthlyUserLimit: z.string().regex(/^\d{1,9}(\.\d{1,2})?$/).nullable(),
  /** KVKK aydınlatma metni kabul edildi mi (açarken zorunlu) */
  privacyAccepted: z.boolean(),
})
export type AiSettingsInput = z.infer<typeof AiSettingsSchema>

/** Aylık AI tüketimini Genel Gider'e işler (`POST /ai/expense-bookings`; AI_ADMIN) */
export const AiExpenseBookingSchema = z.object({
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  category: z.string().trim().min(1).max(30),
  amount: z.string().regex(/^\d{1,13}(\.\d{1,2})?$/),
  kdvRate: z.string().regex(/^\d{1,13}(\.\d{1,2})?$/).nullable().optional(),
})
export type AiExpenseBookingInput = z.infer<typeof AiExpenseBookingSchema>

export interface AiSettingsView {
  enabled: boolean
  monthlyUserLimit: string | null
  provider: AiProviderKind
  baseUrl: string | null
  hasApiKey: boolean
  agentModel: string | null
  lightModel: string | null
  /** portalın son bildirdiği BYOK izni */
  ownProviderAllowed: boolean
  privacyAcceptedAt: string | null
  privacyAcceptedBy: string | null
}

export type AiTurnStatus = 'running' | 'done' | 'error' | 'cancelled'

export interface AiUsageView {
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  /** "0.84" */
  credits: string
  /** "0.0056" */
  usd: string
}

/** Grafik/gösterge değerinin birimi: biçimlendirme istemcide yapılır (tr/en yerel ayarı) */
export type AiValueUnit = 'number' | 'TRY' | 'USD' | 'EUR' | 'percent' | 'hours' | 'days'
export const AI_VALUE_UNITS: readonly AiValueUnit[] = ['number', 'TRY', 'USD', 'EUR', 'percent', 'hours', 'days']
/** Yuvarlak grafikte küçük dilimlerin birleştiği kategori anahtarı; arayüz "Diğer" diye çevirir */
export const AI_OTHER_KEY = '__other__'
export type AiChartType = 'bar' | 'hbar' | 'line' | 'area' | 'stacked' | 'pie'
export const AI_CHART_TYPES: readonly AiChartType[] = ['bar', 'hbar', 'line', 'area', 'stacked', 'pie']

/** Sohbette çizilen grafik: veri sunucuda doğrulanıp anlık görüntü olarak saklanır (cache süresi dolsa da görünür) */
export interface AiChartVisual {
  kind: 'chart'
  type: AiChartType
  title: string
  subtitle: string | null
  unit: AiValueUnit
  /** kategori / zaman ekseni sütununun görünen adı */
  xLabel: string
  series: Array<{ key: string; label: string }>
  /** her satır: { x: kategori, [series.key]: sayı | null } */
  data: Array<Record<string, string | number | null>>
  /** kaynakta daha fazla satır vardı (ilk N gösterildi / kalanı "Diğer"e katıldı) */
  truncatedFrom: number | null
  /** tam tablo (cache anahtarı) — bağlantı için */
  tableKey: string | null
}

export interface AiKpiItem {
  label: string
  value: number
  unit: AiValueUnit
  /** önceki döneme göre değişim (yüzde) */
  delta: number | null
  /** değişimin iyi/kötü yorumu (ör. maliyet artışı kötüdür) */
  deltaGood: 'up' | 'down' | null
  hint: string | null
}
export interface AiKpiVisual { kind: 'kpi'; items: AiKpiItem[] }
export type AiVisual = AiChartVisual | AiKpiVisual

export interface AiToolCallView {
  callId: string
  tool: string
  status: 'running' | 'ok' | 'error'
  /** kullanıcıya gösterilecek kısa özet ("3 satır", "Sorgu hatası: …") */
  summary: string | null
  /** sonuç tablosu (diffusion cache anahtarı) — UI tam veriyi buradan çeker */
  resultKey: string | null
  resultRows: number | null
  /** grafik / gösterge aracının çizilecek çıktısı (yalnızca chart_show, kpi_show) */
  visual: AiVisual | null
}

export type AiPreviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXECUTED' | 'FAILED' | 'EXPIRED' | 'UNDONE'

export interface AiActionPreviewView {
  id: string
  action: string
  method: string
  path: string
  title: string
  danger: boolean
  rowCount: number
  /** ilk birkaç çağrının gövdesi/yolu (kullanıcının ne onayladığını görmesi için) */
  sample: Array<{ path: string; body: unknown }>
  status: AiPreviewStatus
  resultKey: string | null
  okCount: number | null
  errorCount: number | null
  /** Yürütülmüş toplu oluşturma geri alınabilir mi (`POST /ai/previews/:id/undo`) */
  undoable: boolean
  /** Tek kayıt oluşturulduysa kaydın kimliği (detay sayfasına bağlantı için) */
  createdId: string | null
  createdAt: string
}

/** Kullanıcı mesajına eklenen görsel (içerik `GET /ai/attachments/:id` ile alınır) */
export interface AiAttachmentView {
  id: string
  name: string
  mediaType: string
}

export interface AiDocumentView {
  id: string
  name: string
  kind: AiDocKind
  sizeBytes: number
}

/** Şablondan (ya da doğrudan) üretilen çıktı belge */
export interface AiOutputView {
  id: string
  name: string
  kind: AiDocKind
  sizeBytes: number
  templateId: string | null
  createdAt: string
}

export interface AiTemplateField {
  /** küçük harf + rakam + alt çizgi (Word'deki {{alan}}) */
  key: string
  label: string
  description?: string
}

/** Belge şablonu: kaynak belgedeki değişen yerleri {{alan}} ile işaretlenmiş Word dosyası */
export interface AiTemplateView {
  id: string
  name: string
  description: string
  fields: AiTemplateField[]
  sourceDocumentName: string | null
  outputCount: number
  createdAt: string
  updatedAt: string
}

export const AiTemplateFieldSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Alan anahtarı: küçük harf, rakam, alt çizgi'),
  label: z.string().trim().min(1).max(120),
  description: z.string().trim().max(300).optional(),
})
export const AiTemplateUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(500).optional(),
  fields: z.array(AiTemplateFieldSchema).max(60).optional(),
})
export type AiTemplateUpdate = z.infer<typeof AiTemplateUpdateSchema>

export interface AiMessageView {
  id: string
  role: 'user' | 'assistant'
  text: string
  attachments: AiAttachmentView[]
  /** Kullanıcı mesajına eklenen belgeler */
  documents: AiDocumentView[]
  /** Bu asistan mesajındaki araç çağrılarının ürettiği çıktı belgeler */
  outputs: AiOutputView[]
  toolCalls: AiToolCallView[]
  previews: AiActionPreviewView[]
  usage: AiUsageView | null
  createdAt: string
}

export interface AiConversationSummary {
  id: string
  title: string
  updatedAt: string
  credits: string
  messageCount: number
}

export interface AiConversationDetail extends AiConversationSummary {
  messages: AiMessageView[]
  runningTurn: boolean
}

export interface AiStatusView {
  /** tenant ayarı açık mı */
  enabled: boolean
  /** portal erişimi: hesap etkin ve bakiyesi var mı */
  available: boolean
  reason: 'DISABLED' | 'NO_LICENSE' | 'PORTAL_UNREACHABLE' | 'PORTAL_DISABLED' | 'NO_CREDIT' | 'USER_LIMIT' | 'OWN_PROVIDER_NOT_ALLOWED' | 'PROVIDER_NOT_CONFIGURED' | null
  provider: AiProviderKind
  balance: string | null
  monthUserCredits: string
  monthlyUserLimit: string | null
  models: { agent: string; light: string } | null
  /** Seçili sağlayıcı görsel girişi kabul ediyor mu (false → arayüzde görsel ekleme kapalı) */
  vision: boolean
  /** Sesli giriş (dikte) kullanılabilir mi (yalnızca Pusula portalı sağlayıcısında; mikrofon düğmesi buna bağlı) */
  stt: boolean
}

export interface AiCacheTableView {
  key: string
  columns: Array<{ name: string; type: string }>
  rows: Array<Record<string, unknown>>
  total: number
  page: number
  limit: number
}

export interface AiUsageReportRow {
  month: string
  provider: string
  model: string
  userId: string
  userName: string
  purpose: string
  calls: number
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  credits: string
  usd: string
}

/** Socket olayları (`user:{id}` odasına) */
export type AiSocketEvent =
  | { type: 'turn'; conversationId: string; status: AiTurnStatus; error?: string }
  | { type: 'text'; conversationId: string; delta: string }
  | { type: 'tool'; conversationId: string; call: AiToolCallView }
  | { type: 'preview'; conversationId: string; preview: AiActionPreviewView }
  | { type: 'usage'; conversationId: string; usage: AiUsageView; balance: string | null }
  | { type: 'title'; conversationId: string; title: string }
  | { type: 'navigate'; conversationId: string; target: AiNavTarget }
  | { type: 'fill_form'; conversationId: string; target: AiFormFillTarget }
  | { type: 'submit_form'; conversationId: string; form: string }

export const AI_SOCKET_EVENT = 'ai:event'
